/**
 * Performance Monitoring Utility
 * 
 * Tracks Core Web Vitals and custom performance metrics.
 * Sends data to analytics/monitoring service.
 */

interface PerformanceMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  timestamp: number;
}

interface NavigationTiming {
  dnsLookup: number;
  tcpConnection: number;
  tlsHandshake: number;
  ttfb: number;
  downloadTime: number;
  domInteractive: number;
  domComplete: number;
  loadComplete: number;
}

// Thresholds for Core Web Vitals
const THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 },   // Largest Contentful Paint
  FID: { good: 100, poor: 300 },     // First Input Delay
  CLS: { good: 0.1, poor: 0.25 },    // Cumulative Layout Shift
  FCP: { good: 1800, poor: 3000 },   // First Contentful Paint
  TTFB: { good: 800, poor: 1800 },   // Time to First Byte
};

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private observers: PerformanceObserver[] = [];
  private isInitialized = false;

  /**
   * Initialize performance monitoring
   */
  init(): void {
    if (this.isInitialized || typeof window === 'undefined') return;
    
    this.isInitialized = true;

    // Track navigation timing
    this.trackNavigationTiming();

    // Track Core Web Vitals using PerformanceObserver
    this.observeLCP();
    this.observeFID();
    this.observeCLS();
    this.observeFCP();

    // Track resource loading
    this.observeResourceTiming();

    // Log metrics on page unload
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.reportMetrics();
      }
    });

    console.log('[Performance] Monitoring initialized');
  }

  /**
   * Track navigation timing metrics
   */
  private trackNavigationTiming(): void {
    window.addEventListener('load', () => {
      setTimeout(() => {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        
        if (!navigation) return;

        const timing: NavigationTiming = {
          dnsLookup: navigation.domainLookupEnd - navigation.domainLookupStart,
          tcpConnection: navigation.connectEnd - navigation.connectStart,
          tlsHandshake: navigation.secureConnectionStart > 0 
            ? navigation.connectEnd - navigation.secureConnectionStart 
            : 0,
          ttfb: navigation.responseStart - navigation.requestStart,
          downloadTime: navigation.responseEnd - navigation.responseStart,
          domInteractive: navigation.domInteractive - navigation.startTime,
          domComplete: navigation.domComplete - navigation.startTime,
          loadComplete: navigation.loadEventEnd - navigation.startTime,
        };

        // Add TTFB as a metric
        this.addMetric('TTFB', timing.ttfb, THRESHOLDS.TTFB);

        console.log('[Performance] Navigation Timing:', timing);
        
        // Store for debugging
        (window as any).__NEURON_PERF_TIMING__ = timing;
      }, 0);
    });
  }

  /**
   * Observe Largest Contentful Paint
   */
  private observeLCP(): void {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1] as PerformanceEntry & { startTime: number };
        
        if (lastEntry) {
          this.addMetric('LCP', lastEntry.startTime, THRESHOLDS.LCP);
        }
      });

      observer.observe({ type: 'largest-contentful-paint', buffered: true });
      this.observers.push(observer);
    } catch (e) {
      console.warn('[Performance] LCP observation not supported');
    }
  }

  /**
   * Observe First Input Delay
   */
  private observeFID(): void {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const firstEntry = entries[0] as PerformanceEntry & { processingStart: number; startTime: number };
        
        if (firstEntry) {
          const fid = firstEntry.processingStart - firstEntry.startTime;
          this.addMetric('FID', fid, THRESHOLDS.FID);
        }
      });

      observer.observe({ type: 'first-input', buffered: true });
      this.observers.push(observer);
    } catch (e) {
      console.warn('[Performance] FID observation not supported');
    }
  }

  /**
   * Observe Cumulative Layout Shift
   */
  private observeCLS(): void {
    try {
      let clsValue = 0;
      let sessionValue = 0;
      let sessionEntries: PerformanceEntry[] = [];

      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries() as (PerformanceEntry & { hadRecentInput: boolean; value: number })[]) {
          if (!entry.hadRecentInput) {
            const firstSessionEntry = sessionEntries[0] as PerformanceEntry | undefined;
            const lastSessionEntry = sessionEntries[sessionEntries.length - 1] as PerformanceEntry | undefined;

            if (
              sessionValue &&
              firstSessionEntry &&
              lastSessionEntry &&
              entry.startTime - lastSessionEntry.startTime < 1000 &&
              entry.startTime - firstSessionEntry.startTime < 5000
            ) {
              sessionValue += entry.value;
              sessionEntries.push(entry);
            } else {
              sessionValue = entry.value;
              sessionEntries = [entry];
            }

            if (sessionValue > clsValue) {
              clsValue = sessionValue;
              this.addMetric('CLS', clsValue, THRESHOLDS.CLS);
            }
          }
        }
      });

      observer.observe({ type: 'layout-shift', buffered: true });
      this.observers.push(observer);
    } catch (e) {
      console.warn('[Performance] CLS observation not supported');
    }
  }

  /**
   * Observe First Contentful Paint
   */
  private observeFCP(): void {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const fcpEntry = entries.find(e => e.name === 'first-contentful-paint');
        
        if (fcpEntry) {
          this.addMetric('FCP', fcpEntry.startTime, THRESHOLDS.FCP);
        }
      });

      observer.observe({ type: 'paint', buffered: true });
      this.observers.push(observer);
    } catch (e) {
      console.warn('[Performance] FCP observation not supported');
    }
  }

  /**
   * Observe resource loading times
   */
  private observeResourceTiming(): void {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries() as PerformanceResourceTiming[];
        
        entries.forEach(entry => {
          // Track slow resources (> 1 second)
          if (entry.duration > 1000) {
            console.warn(`[Performance] Slow resource: ${entry.name} (${Math.round(entry.duration)}ms)`);
          }
        });
      });

      observer.observe({ type: 'resource', buffered: true });
      this.observers.push(observer);
    } catch (e) {
      console.warn('[Performance] Resource timing observation not supported');
    }
  }

  /**
   * Add a metric with rating
   */
  private addMetric(
    name: string, 
    value: number, 
    threshold: { good: number; poor: number }
  ): void {
    const rating = value <= threshold.good 
      ? 'good' 
      : value <= threshold.poor 
        ? 'needs-improvement' 
        : 'poor';

    const metric: PerformanceMetric = {
      name,
      value: Math.round(value * 100) / 100,
      rating,
      timestamp: Date.now(),
    };

    // Update existing metric or add new one
    const existingIndex = this.metrics.findIndex(m => m.name === name);
    if (existingIndex >= 0) {
      this.metrics[existingIndex] = metric;
    } else {
      this.metrics.push(metric);
    }

    // Log to console with color coding
    const color = rating === 'good' ? 'green' : rating === 'needs-improvement' ? 'orange' : 'red';
    console.log(`[Performance] ${name}: %c${metric.value}ms (${rating})`, `color: ${color}`);
  }

  /**
   * Custom timing mark
   */
  mark(name: string): void {
    performance.mark(name);
  }

  /**
   * Measure between two marks
   */
  measure(name: string, startMark: string, endMark?: string): number | null {
    try {
      if (endMark) {
        performance.measure(name, startMark, endMark);
      } else {
        performance.measure(name, startMark);
      }
      
      const entries = performance.getEntriesByName(name, 'measure');
      const lastEntry = entries[entries.length - 1];
      
      if (lastEntry) {
        console.log(`[Performance] ${name}: ${Math.round(lastEntry.duration)}ms`);
        return lastEntry.duration;
      }
    } catch (e) {
      console.warn(`[Performance] Failed to measure ${name}`);
    }
    return null;
  }

  /**
   * Track API call performance
   */
  trackApiCall(endpoint: string, startTime: number): void {
    const duration = performance.now() - startTime;
    const rating = duration < 500 ? 'good' : duration < 2000 ? 'needs-improvement' : 'poor';
    
    console.log(
      `[Performance] API ${endpoint}: %c${Math.round(duration)}ms`,
      `color: ${rating === 'good' ? 'green' : rating === 'needs-improvement' ? 'orange' : 'red'}`
    );
  }

  /**
   * Report all collected metrics
   */
  private reportMetrics(): void {
    if (this.metrics.length === 0) return;

    console.table(this.metrics);

    // Store for debugging
    (window as any).__NEURON_PERF_METRICS__ = this.metrics;

    // Here you could send to Sentry, Google Analytics, or custom analytics
    // Example with Sentry:
    // if (typeof Sentry !== 'undefined') {
    //   this.metrics.forEach(m => {
    //     Sentry.setMeasurement(m.name, m.value, 'millisecond');
    //   });
    // }
  }

  /**
   * Get current metrics
   */
  getMetrics(): PerformanceMetric[] {
    return [...this.metrics];
  }

  /**
   * Clean up observers
   */
  destroy(): void {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
    this.isInitialized = false;
  }
}

// Export singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Auto-initialize in browser
if (typeof window !== 'undefined') {
  // Initialize after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => performanceMonitor.init());
  } else {
    performanceMonitor.init();
  }
}

export default performanceMonitor;
